import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { Plus, X } from "react-feather";
import { CloseButton } from "components/CloseButton";
import { toast } from "react-toastify";
import { baseUrl } from "services/api";
import {
  deleteSupplyProductDocument,
  saveSupplyProductReclamation,
  uploadSupplyProductDocuments,
} from "services/apiSupply";
import { getSupplyProductImageFiles } from "../supplyProductFiles";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/dist/styles.css";

import "./supplyProductReclamationModal.scss";

const REASON_OPTIONS = [
  { value: "nonconformity", label: "Некондиция" },
  { value: "shortage", label: "Недопоставка" },
  { value: "surplus", label: "Излишек" },
];

export const SupplyProductReclamationModal = ({
  isOpen,
  onClose,
  supplyId,
  productRow,
  products,
  setProducts,
  setLoading,
}) => {
  const fileInputRef = useRef(null);
  const wasModalOpenRef = useRef(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [photoLightbox, setPhotoLightbox] = useState({
    open: false,
    index: 0,
    slides: [],
  });

  const currentRow =
    productRow && products?.length
      ? products.find((p) => p.id === productRow.id) ?? productRow
      : productRow;

  const imageFiles = currentRow ? getSupplyProductImageFiles(currentRow) : [];

  useEffect(() => {
    if (isOpen && !wasModalOpenRef.current && productRow) {
      const row =
        products?.find((p) => p.id === productRow.id) ?? productRow;
      const rec = row?.reclamation;
      const r = rec?.reason;
      setReason(
        r && REASON_OPTIONS.some((o) => o.value === r) ? r : "",
      );
      setComment(typeof rec?.comment === "string" ? rec.comment : "");
    }
    wasModalOpenRef.current = isOpen;
  }, [isOpen, productRow, products]);

  useEffect(() => {
    if (!isOpen) {
      setPhotoLightbox((s) => ({ ...s, open: false }));
    }
  }, [isOpen]);

  const openLightboxAtKey = (key) => {
    const imgs = imageFiles.filter((f) => f.key);
    const index = imgs.findIndex((f) => f.key === key);
    if (index < 0) {
      return;
    }
    setPhotoLightbox({
      open: true,
      index,
      slides: imgs.map((f) => ({
        src: `${baseUrl}/image.view?file=${encodeURIComponent(f.key)}`,
        alt: f.name || "",
      })),
    });
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    const imgs = picked.filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) {
      if (picked.length) {
        toast.error("Допустимы только изображения");
      }
      return;
    }
    if (!supplyId || !currentRow?.id) {
      toast.error("Сохраните поступление перед загрузкой");
      return;
    }

    setLoading?.(true);
    uploadSupplyProductDocuments(supplyId, currentRow.id, imgs)
      .then((nextProducts) => {
        setProducts(nextProducts);
        toast.success("Фото загружены");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Ошибка загрузки");
      })
      .finally(() => setLoading?.(false));
  };

  const removeFile = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!supplyId || !currentRow?.id) {
      return;
    }
    setLoading?.(true);
    deleteSupplyProductDocument(supplyId, currentRow.id, key)
      .then((nextProducts) => {
        setProducts(nextProducts);
        toast.success("Файл удалён");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Ошибка удаления");
      })
      .finally(() => setLoading?.(false));
  };

  const handleSubmit = () => {
    if (!supplyId || !currentRow?.id) {
      toast.error("Сохраните поступление перед сохранением рекламации");
      return;
    }
    if (!reason || !REASON_OPTIONS.some((o) => o.value === reason)) {
      toast.error("Выберите причину рекламации");
      return;
    }
    setSubmitting(true);
    setLoading?.(true);
    saveSupplyProductReclamation(supplyId, currentRow.id, {
      reason,
      comment,
    })
      .then((nextProducts) => {
        setProducts(nextProducts);
        toast.success("Сохранено");
        onClose();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Ошибка сохранения");
      })
      .finally(() => {
        setSubmitting(false);
        setLoading?.(false);
      });
  };

  if (!productRow) {
    return null;
  }

  const brand =
    currentRow.nomenclature?.brand ?? currentRow.brand ?? "";
  const article =
    currentRow.nomenclature?.article ?? currentRow.article ?? "";
  const name = currentRow.nomenclature?.name ?? currentRow.name ?? "";

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      className="supply-product-reclamation-modal"
    >
      <ModalHeader>
        <div className="modal-title-text">Рекламация по строке</div>
        <CloseButton onClick={onClose} />
      </ModalHeader>
      <ModalBody className="m-3">
        <div className="supply-product-reclamation-modal__product-line">
          <strong>
            {brand ? `${brand} ` : ""}
            {article}
          </strong>
          {name ? ` — ${name}` : ""}
        </div>

        <FormGroup>
          <Label className="font-weight-bold">Причина</Label>
          <div className="supply-product-reclamation-modal__reason-group">
            {REASON_OPTIONS.map((opt) => (
              <div key={opt.value} className="form-check">
                <Input
                  className="form-check-input"
                  type="radio"
                  id={`supply-recl-reason-${opt.value}`}
                  name="supplyProductReclamationReason"
                  checked={reason === opt.value}
                  onChange={() => setReason(opt.value)}
                />
                <Label className="form-check-label" for={`supply-recl-reason-${opt.value}`}>
                  {opt.label}
                </Label>
              </div>
            ))}
          </div>
        </FormGroup>

        <FormGroup>
          <Label for="supply-recl-comment">Комментарий</Label>
          <Input
            id="supply-recl-comment"
            type="textarea"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Детали по позиции (необязательно)"
          />
        </FormGroup>

        <div className="d-flex justify-content-end mb-3">
          <Button color="secondary" className="mr-2" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={submitting}>
            Сохранить
          </Button>
        </div>

        <FormGroup>
          <Label>Фото — клик по превью открывает просмотр</Label>

          <Input
            type="file"
            accept="image/*"
            multiple
            hidden
            innerRef={fileInputRef}
            onChange={onPickFiles}
          />

          <div className="supply-product-reclamation-modal__thumbs">
            {imageFiles.map((f) => {
              const key = f.key;
              if (!key) {
                return null;
              }
              return (
                <div key={key} className="supply-product-reclamation-modal__thumb-wrap">
                  <button
                    type="button"
                    className="supply-product-reclamation-modal__thumb"
                    onClick={() => openLightboxAtKey(key)}
                    title="Открыть галерею"
                  >
                    <img
                      src={`${baseUrl}/image.view?file=${encodeURIComponent(key)}`}
                      alt=""
                    />
                  </button>
                  <button
                    type="button"
                    className="supply-product-reclamation-modal__thumb-remove"
                    title="Удалить файл"
                    aria-label="Удалить файл"
                    onClick={(e) => removeFile(key, e)}
                  >
                    <X size={14} strokeWidth={2.5} />
                  </button>
                </div>
              );
            })}

            <div className="supply-product-reclamation-modal__upload-tile">
              <Button
                color="outline-secondary"
                className="supply-product-reclamation-modal__upload-btn"
                type="button"
                disabled={!supplyId || !currentRow?.id}
                onClick={() => fileInputRef.current?.click()}
              >
                <Plus size={20} className="mr-1" strokeWidth={2} />
                Загрузить фото
              </Button>
            </div>
          </div>
        </FormGroup>
      </ModalBody>

      <Lightbox
        open={photoLightbox.open}
        close={() => setPhotoLightbox((s) => ({ ...s, open: false }))}
        index={photoLightbox.index}
        slides={photoLightbox.slides}
        on={{
          view: ({ index: i }) => setPhotoLightbox((s) => ({ ...s, index: i })),
        }}
      />
    </Modal>
  );
};
